import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Input, ViewChild } from '@angular/core';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { timer } from 'rxjs';
import { DeviceAttribute } from '../../shared/models/device-attribute.model';
import { DeviceService } from '../../shared/services/device.service';
import { DeviceDetailService } from '../../shared/services/device-detail.service';

@Component({
  selector: 'app-device-sensitive',
  templateUrl: './device-sensitive.component.html',
  styleUrls: ['./device-sensitive.component.scss'],
  providers: [
    DeviceDetailService,
    { provide: 'tablePaddingHeight', useValue: 216 },
    { provide: 'itemHeight', useValue: 52 },
    { provide: 'paginations', useValue: [5, 10, 25, 50] },
  ],
})
export class DeviceSensitiveComponent {
  protected mobile: boolean = false;
  protected loading: boolean = true;
  protected showSensitive: string | undefined;

  @Input() set externalDeviceId(value: string | undefined) {
    if (value === undefined) return;
    this.loading = true;
    this.deviceService
      .getDeviceSensitiveDataByExternalDeviceId(value ?? '')
      .subscribe({
        next: (data) => {
          this.deviceAttributes.data = data.device.attributes ?? [];
        },
      })
      .add(() => {
        this.loading = false;
        timer(0).subscribe(() => {
          this.deviceAttributes.paginator = this.deviceAttributesPaginator;
          this.deviceAttributes.sort = this.deviceAttributesSort;
          this.calcPagination();
        });
      });
  }

  protected readonly deviceAttributes: MatTableDataSource<DeviceAttribute> =
    new MatTableDataSource<DeviceAttribute>();
  @ViewChild('deviceAttributesPaginator')
  private deviceAttributesPaginator!: MatPaginator;
  @ViewChild('deviceAttributesTable') private deviceAttributesSort =
    new MatSort();
  @ViewChild('deviceAttributesGroup') deviceAttributesGroup:
    | MatButtonToggle
    | undefined;

  private readonly deviceAttributesColumns: (
    | keyof DeviceAttribute
    | 'actions'
  )[] = [
    'attributeNum',
    'attributeName',
    'valueDisplayText',
    'attributeType',
    'lastUpdatedTimestamp',
    'actions',
  ];
  protected deviceAttributesColumnsToggle: (keyof DeviceAttribute)[] = [
    'attributeName',
    'valueDisplayText',
    'lastUpdatedTimestamp',
  ];
  protected get filteredDeviceAttributesColumns(): (
    | keyof DeviceAttribute
    | 'actions'
  )[] {
    return this.deviceAttributesColumns.filter(
      (col) =>
        col === 'actions' || this.deviceAttributesColumnsToggle.includes(col)
    );
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private deviceService: DeviceService,
    private deviceDetailService: DeviceDetailService
  ) {
    this.deviceAttributes.sortData = this.sortDataDeviceAttributes();
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        this.mobile = result.matches;
        if (result.matches) {
          this.deviceAttributesColumnsToggle = [
            'attributeName',
            'valueDisplayText',
            'lastUpdatedTimestamp',
          ];
        }
        this.calcPagination();
      },
    });
  }

  private sortDataDeviceAttributes() {
    return (items: DeviceAttribute[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort((a, b) => {
        const asc = sort.direction === 'asc' ? 1 : -1;
        const valA: string | number =
          a[sort.active as keyof DeviceAttribute] ?? '';
        const valB: string | number =
          b[sort.active as keyof DeviceAttribute] ?? '';
        if (sort.active === 'valueDisplayText') {
          const valANum = Number(a.attributeValueNumeric);
          const valBNum = Number(b.attributeValueNumeric);
          if (!isNaN(valANum) && !isNaN(valBNum)) {
            return (valANum - valBNum) * asc;
          }
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return (valA - valB) * asc;
        }
        return String(valA).localeCompare(String(valB)) * asc;
      });
    };
  }

  private calcPagination() {
    if (this.deviceAttributesPaginator) {
      this.deviceDetailService.calculatePagination(
        this.deviceAttributesPaginator
      );
      this.deviceAttributes.paginator = this.deviceAttributesPaginator;
    }
  }
}
